import React, { useState, useCallback } from "react";
import {
    TextField,
    Typography,
    Stack,
    IconButton,
    Alert,
    Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useForm } from "react-hook-form";
import axiosInstance from "../api/axiosInstance";
import { usePostHog } from "posthog-js/react";
import { toast } from "react-toastify";
import UncontrolledAutocomplete from "./Custom/UncontrolledAutocomplete";
import CustomButton from "./Custom/CustomButton";
import CustomUpload from "./Custom/CustomUpload";
import { MAX_STATUS_ATTACHMENTS, isCompletedStatus } from "../utils/taskUtils";
import { POSTHOG_EVENTS } from "../constants/posthog-events";
import "./TaskBoard.css";
import "./ModalStyles.css";

// ─────────────────────────────────────────────────────────────────────────────
// Status options
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
    { value: "pending", label: "Pending" },
    { value: "planned", label: "Planned" },
    { value: "WIP", label: "WIP" },
    { value: "completed", label: "Completed" },
    { value: "revalidate", label: "Revalidate" },
    { value: "delegated", label: "Delegate" },
    { value: "reopen", label: "Reopen" },
];

// ─────────────────────────────────────────────────────────────────────────────
// TaskStatusModal
//
// Props:
//   task             {object}   — The task being updated (.id, .status)
//   userId           {string}   — Current user's id
//   delegateOptions  {array}    — [{value, label, meta:{email}, searchText}]
//   onClose          {fn}       — Called to close without saving
//   onSuccess        {fn(msg, newStatus)} — Called with success message after update
//   onCompleted      {fn(task, newStatus)} — Called when status changes to Completed
// ─────────────────────────────────────────────────────────────────────────────

const TaskStatusModal = ({ task, userId, delegateOptions = [], loadingEmployees = false, onClose, onSuccess, onCompleted }) => {
    const posthog = usePostHog();

    // ── Internal form state ───────────────────────────────────────────────────
    const [selectedStatus, setSelectedStatus] = useState(
        task ? STATUS_OPTIONS.find((o) => o.value?.toLowerCase() === (task.status || "").toLowerCase()) ?? STATUS_OPTIONS[0] : null
    );
    const [statusRemark, setStatusRemark] = useState("");
    const [delegationDraft, setDelegationDraft] = useState({ employee: null, taskName: "", description: "" });
    const [delegationEntries, setDelegationEntries] = useState([]);
    const [delegationDraftError, setDelegationDraftError] = useState("");
    const [updateLoading, setUpdateLoading] = useState(false);
    const [updateError, setUpdateError] = useState("");

    // ── react-hook-form for attachments ────────────────────────────────────────
    const { control, getValues, watch } = useForm({
        defaultValues: {
            statusAttachments: []
        }
    });

    const watchedAttachments = watch("statusAttachments") || [];

    // ── Delegation handlers ───────────────────────────────────────────────────
    const handleAddDelegationEntry = () => {
        if (!delegationDraft.employee || !delegationDraft.taskName.trim()) {
            setDelegationDraftError("Employee and Task Name are required.");
            return;
        }
        setDelegationEntries((prev) => [...prev, { ...delegationDraft, id: Date.now() }]);
        setDelegationDraft({ employee: null, taskName: "", description: "" });
        setDelegationDraftError("");
    };

    const handleRemoveDelegationEntry = (id) =>
        setDelegationEntries((prev) => prev.filter((e) => e.id !== id));



    // ── Employee option renderer ──────────────────────────────────────────────
    const renderEmployeeOption = useCallback((props, option) => (
        <li {...props} key={option.value}>
            <Stack>
                <Typography variant="body2" fontWeight={600} fontSize="0.88rem">
                    {option.name || option.label}
                </Typography>
                {(option.email || option.meta?.email) && (
                    <Typography variant="caption" color="text.secondary">
                        {option.email || option.meta?.email}
                    </Typography>
                )}
            </Stack>
        </li>
    ), []);

    // Match Add Task assign-to search: name, email, and department (not name-only).
    const filterEmployeeOptions = useCallback((options, { inputValue }) => {
        const q = inputValue.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => {
            const haystack = (
                o.searchText ||
                `${o.name || o.label || ""} ${o.email || o.meta?.email || ""} ${o.department || ""}`
            ).toLowerCase();
            return haystack.includes(q);
        });
    }, []);

    // ── Early return after all hooks ──────────────────────────────────────────
    if (!task) return null;

    const isUnchanged = selectedStatus?.value?.toLowerCase() === (task.status || "").toLowerCase();
    const isDelegated = String(selectedStatus?.value)?.toLowerCase() === "delegated";
    const canSubmit =
        !!selectedStatus &&
        (!isUnchanged || watchedAttachments.length > 0 || statusRemark.trim().length > 0 || (isDelegated && delegationEntries.length > 0)) &&
        (!isDelegated || delegationEntries.length > 0) &&
        !updateLoading;

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleUpdate = async () => {
        if (!canSubmit) return;
        setUpdateLoading(true);
        setUpdateError("");
        try {
            const formData = new FormData();
            formData.append("status", selectedStatus.value);
            // if (userId) formData.append("currentUserId", userId);
            if (statusRemark) formData.append("status_remark", statusRemark);

            // Only append delegations when status is "delegated"
            if (isDelegated) {
                formData.append("delegations", JSON.stringify(
                    delegationEntries.map((e) => ({
                        taskName: e.taskName,
                        assigned_to: e.employee.value,
                        description: e.description
                    }))
                ));
            }
            const attachments = getValues("statusAttachments") || [];
            attachments.forEach((f) => formData.append("taskAttachments", f));

            await axiosInstance.put(`/api/task/${task.id}`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });

            const previousStatus = (task.status || "").toString().toLowerCase();
            const newStatus = (selectedStatus.value || "").toString().toLowerCase();
            posthog?.capture(POSTHOG_EVENTS.TASK_STATUS_CHANGED, {
                task_id: task.id,
                previous_value: previousStatus,
                new_value: newStatus,
                has_remark: Boolean(statusRemark?.trim()),
                updated_by: userId || null,
            });

            if (isDelegated && delegationEntries.length > 0) {
                posthog?.capture(POSTHOG_EVENTS.TASK_DELEGATED, {
                    task_id: task.id,
                    delegation_count: delegationEntries.length,
                    delegated_by: userId || null,
                });
                delegationEntries.forEach((entry) => {
                    posthog?.capture(POSTHOG_EVENTS.SUBTASK_CREATED, {
                        parent_task_id: task.id,
                        title: entry.taskName,
                        assignee_id: entry.employee?.value || null,
                        created_by: userId || null,
                        source: "delegation",
                    });
                });
            }

            const statusAttachments = getValues("statusAttachments") || [];
            if (statusAttachments.length > 0) {
                posthog?.capture(POSTHOG_EVENTS.ATTACHMENT_ADDED, {
                    task_id: task.id,
                    attachments_count: statusAttachments.length,
                    source: "status_change",
                    uploaded_by: userId || null,
                });
            }

            onSuccess?.("Task updated successfully!", selectedStatus.value);
            if (isCompletedStatus(selectedStatus.value)) {
                onCompleted?.(task, selectedStatus.value);
            }
            onClose();
        } catch (e) {
            const errMessage = e?.response?.data?.message || e.message || "Update failed. Please try again.";
            setUpdateError(errMessage);
            toast.error(errMessage);
        } finally {
            setUpdateLoading(false);
        }
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="action-modal-overlay">
            <div
                className="modal-content-base"
                style={{ maxWidth: 540 }}
            >
                {/* ── Header ── */}
                <div className="modal-header-base">
                    <h3 className="modal-title-base">Change Task Status</h3>
                    <button className="modal-close-base" onClick={onClose} title="Close">
                        <CloseIcon fontSize="small" />
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="modal-body-base">
                    <Stack spacing={2.5}>

                        {/* Task name context */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography variant="body2" color="text.secondary">Task:</Typography>
                            <Typography variant="body2" fontWeight={600} noWrap title={task.title}>
                                {task.title}
                            </Typography>
                        </Stack>

                        {/* Status selector */}
                        <UncontrolledAutocomplete
                            label="New Status"
                            placeholder="Select a status"
                            options={STATUS_OPTIONS}
                            value={selectedStatus}
                            onChange={setSelectedStatus}
                            disableClearable
                            slotProps={{
                                listbox: { style: { fontSize: "12px" } },
                                paper: { sx: { fontSize: "12px" } },
                            }}
                        />

                        {/* ── Conditional sections ── */}
                        {/* Delegation section */}
                        {String(selectedStatus?.value)?.toLowerCase() === "delegated" && (
                            <Stack spacing={1.5}>
                                <Typography variant="subtitle2" fontWeight={700} color="primary">
                                    Tasks Delegation
                                </Typography>

                                {/* Delegation form card */}
                                <Stack
                                    spacing={1.5}
                                    sx={{
                                        border: "1px solid #e0e7ff",
                                        borderRadius: 3,
                                        p: 2,
                                        bgcolor: "#f9f9ff",
                                    }}
                                >
                                    <UncontrolledAutocomplete
                                        label="Assign To"
                                        placeholder="Search employee…"
                                        options={delegateOptions}
                                        value={delegationDraft.employee}
                                        onChange={(val) =>
                                            setDelegationDraft((prev) => ({ ...prev, employee: val }))
                                        }
                                        renderOption={renderEmployeeOption}
                                        filterOptions={filterEmployeeOptions}
                                        loading={loadingEmployees}
                                        error={delegationDraftError && !delegationDraft.employee ? delegationDraftError : ""}
                                    />
                                    <TextField
                                        label="Task Name"
                                        size="small"
                                        fullWidth
                                        value={delegationDraft.taskName}
                                        onChange={(e) =>
                                            setDelegationDraft((prev) => ({ ...prev, taskName: e.target.value }))
                                        }
                                        error={Boolean(delegationDraftError && !delegationDraft.taskName.trim())}
                                        slotProps={{ htmlInput: { style: { fontSize: "12px" } } }}
                                    />
                                    <TextField
                                        label="Description (optional)"
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={2}
                                        value={delegationDraft.description}
                                        onChange={(e) =>
                                            setDelegationDraft((prev) => ({ ...prev, description: e.target.value }))
                                        }
                                        slotProps={{ htmlInput: { style: { fontSize: "12px" } } }}
                                    />
                                    {delegationDraftError && (
                                        <Alert severity="error" sx={{ py: 0.5, fontSize: "0.8rem" }}>
                                            {delegationDraftError}
                                        </Alert>
                                    )}
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Typography variant="caption" color="text.secondary">
                                            Click <strong>Add</strong> to queue another task.
                                        </Typography>
                                        <CustomButton
                                            size="small"
                                            variant="contained"
                                            fullWidth={false}
                                            onClick={handleAddDelegationEntry}
                                            sx={{ borderRadius: "999px", px: 2.5, textTransform: "none" }}
                                        >
                                            Add
                                        </CustomButton>
                                    </Stack>
                                </Stack>

                                {/* Delegation queue */}
                                {delegationEntries.length > 0 && (
                                    <Stack spacing={1}>
                                        <Typography
                                            variant="caption"
                                            fontWeight={700}
                                            color="primary"
                                            sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                                        >
                                            Delegation Queue ({delegationEntries.length})
                                        </Typography>
                                        {delegationEntries.map((entry) => (
                                            <Stack
                                                key={entry.id}
                                                sx={{
                                                    bgcolor: "#fff",
                                                    border: "1px solid #e0e7ff",
                                                    borderRadius: 2,
                                                    p: 1.5,
                                                    boxShadow: "0 2px 8px rgba(76,81,191,0.07)",
                                                }}
                                                spacing={0.5}
                                            >
                                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                    <Stack>
                                                        <Typography variant="body2" fontWeight={700} fontSize="0.88rem">
                                                            {entry.employee?.label || "Unnamed"}
                                                        </Typography>
                                                        {entry.employee?.meta?.email && (
                                                            <Typography variant="caption" color="text.secondary">
                                                                {entry.employee.meta.email}
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleRemoveDelegationEntry(entry.id)}
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                                <Typography variant="body2" fontWeight={600} fontSize="0.85rem">
                                                    {entry.taskName}
                                                </Typography>
                                                {entry.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {entry.description}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        ))}
                                    </Stack>
                                )}
                            </Stack>
                        )}

                        <Divider />

                        {/* Remark */}
                        <Stack spacing={0.5}>
                            <TextField
                                label={`Remark for ${selectedStatus?.label || "status"} status`}
                                size="small"
                                fullWidth
                                multiline
                                rows={3}
                                value={statusRemark}
                                onChange={(e) => setStatusRemark(e.target.value)}
                                inputProps={{ maxLength: 2000 }}
                                slotProps={{ htmlInput: { style: { fontSize: "12px" } } }}
                            />
                            <Typography variant="caption" color="text.secondary" align="right">
                                {statusRemark.length}/2000
                            </Typography>
                        </Stack>

                        {/* Attachments */}
                        <Stack spacing={1}>
                            <Typography variant="body2" fontWeight={500} color="text.secondary">
                                Supporting files (optional)
                            </Typography>
                            <CustomUpload
                                control={control}
                                name="statusAttachments"
                                isMultiple={true}
                                extensions={["jpg", "jpeg", "png", "pdf", "doc", "docx", "xls", "xlsx", "csv", "zip"]}
                                labelSize="small"
                                size="small"
                                maxFileSize={15728640}
                            />
                        </Stack>

                        {/* Error */}
                        {updateError && (
                            <Alert severity="error" sx={{ fontSize: "0.85rem" }}>{updateError}</Alert>
                        )}

                        {/* Owner note */}
                        <Alert severity="info" icon={false} sx={{ fontSize: "0.8rem", py: 0.5 }}>
                            Editing and deletion of tasks is managed by the task owner. You can still update the status or add remarks here.
                        </Alert>
                    </Stack>
                </div>

                {/* ── Footer ── */}
                <div className="modal-footer-base" style={{ justifyContent: "flex-end", gap: 12 }}>
                    <CustomButton
                        variant="outlined"
                        color="inherit"
                        fullWidth={false}
                        onClick={onClose}
                        sx={{ textTransform: "none", borderRadius: 2, minWidth: 90 }}
                    >
                        Cancel
                    </CustomButton>
                    <CustomButton
                        variant="contained"
                        fullWidth={false}
                        loading={updateLoading}
                        loadingText="Updating…"
                        disabled={!canSubmit}
                        onClick={handleUpdate}
                        sx={{ textTransform: "none", borderRadius: 2, minWidth: 110 }}
                    >
                        Update Status
                    </CustomButton>
                </div>
            </div>
        </div>
    );
};

export default TaskStatusModal;
